import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { isInactivePayslipExceptionEmail } from '../../../utils/payrollUtils';
import SavedPayslipHeader from './SavedPayslipHeader';

export const n = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseFloat(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => `₱${n(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const itemLabel = (item, fallback) => String(item?.title || item?.description || '').trim() || fallback;

const normalizeLoanText = (value) => String(value || '').trim().toLowerCase();

const looksLikeLoanDeductionItem = (item, knownLabels = new Set()) => {
  if (!item) return false;
  if (item?.is_loan_deduction === true) return true;

  const loanId = String(item?.loan_id || '').trim();
  if (loanId) return true;

  const title = normalizeLoanText(item?.title);
  const description = normalizeLoanText(item?.description);
  const hay = `${title} ${description}`.trim();

  if (title.startsWith('loan - ')) {
    const rawLabel = title.slice('loan - '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }
  if (description.startsWith('loan deduction for ')) {
    const rawLabel = description.slice('loan deduction for '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }

  if (knownLabels.size > 0) {
    for (const label of knownLabels) {
      if (label && hay.includes(label)) return true;
    }
  }

  return hay.includes('loan');
};

const loanLabel = (loan, index) => {
  const baseLabel = String(
    loan?.loan_label
    || loan?.loan_subtype
    || loan?.title
    || loan?.description
    || ''
  ).trim();
  return baseLabel || `Loan ${index + 1}`;
};

const loanRowAmount = (row) => {
  if (!row) return 0;
  const v = row?.deducted_amount ?? row?.scheduled_amount ?? row?.deduct_per_cutoff ?? row?.amount;
  return n(v);
};

const sumItemAmounts = (items) => {
  const list = Array.isArray(items) ? items : [];
  return list.reduce((sum, item) => sum + n(item?.amount), 0);
};

export const normalizePayrollLoanSummary = (loanSummary) => {
  const rows = Array.isArray(loanSummary?.rows) ? loanSummary.rows : [];
  const enabledRows = rows.filter((row) => {
    if (String(row?.cycle_status || '').trim().toLowerCase() === 'skipped') return false;
    if (row?.isSelected === false) return false;
    return loanRowAmount(row) > 0;
  });
  const loanFormTypes = enabledRows
    .map((row) => String(row?.loan_label || row?.loan_subtype || row?.title || row?.description || '').trim())
    .filter(Boolean);
  const loanDeductionPerCutoffTotal = enabledRows.reduce((sum, row) => sum + loanRowAmount(row), 0);
  return {
    rows: enabledRows,
    loanFormTypes,
    loanDeductionPerCutoffTotal
  };
};

export const buildPayrollLoanSummaryFromRecord = (record) => {
  const loanCycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
  const knownLabels = new Set(
    loanCycles
      .map((cycle) => normalizeLoanText(cycle?.loan_label))
      .filter(Boolean)
  );
  const generatedLoanDeductions = (Array.isArray(record?.additional_deductions) ? record.additional_deductions : [])
    .filter((item) => looksLikeLoanDeductionItem(item, knownLabels))
    .map((item) => ({
      loan_id: item?.loan_id,
      loan_label: item?.loan_label,
      title: item?.title,
      description: item?.description,
      amount: item?.amount,
      deducted_amount: item?.deducted_amount
    }));
  if (loanCycles.length === 0 && generatedLoanDeductions.length === 0) return null;

  const mergedRows = [...loanCycles];
  const seenKeys = new Set(
    loanCycles.map((cycle) => {
      const amount = cycle?.deducted_amount ?? cycle?.scheduled_amount ?? cycle?.deduct_per_cutoff ?? cycle?.amount;
      return `${String(cycle?.loan_id || '').trim()}|${normalizeLoanText(cycle?.loan_label)}|${n(amount).toFixed(2)}`;
    })
  );

  generatedLoanDeductions.forEach((item) => {
    const amount = item?.deducted_amount ?? item?.scheduled_amount ?? item?.deduct_per_cutoff ?? item?.amount;
    const key = `${String(item?.loan_id || '').trim()}|${normalizeLoanText(item?.loan_label || item?.title || item?.description)}|${n(amount).toFixed(2)}`;
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    mergedRows.push(item);
  });

  return normalizePayrollLoanSummary({ rows: mergedRows });
};

const isPersistedGeneratedLoanDeductionItem = (item, record) => {
  if (!item) return false;
  const loanCycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
  const knownLabels = new Set(
    loanCycles
      .map((cycle) => normalizeLoanText(cycle?.loan_label))
      .filter(Boolean)
  );
  if (looksLikeLoanDeductionItem(item, knownLabels)) return true;
  const loanId = String(item?.loan_id || '').trim();
  const knownLoanIds = new Set(loanCycles.map((cycle) => String(cycle?.loan_id || '').trim()).filter(Boolean));
  if (loanId && knownLoanIds.has(loanId)) return true;
  const title = normalizeLoanText(item?.title);
  const description = normalizeLoanText(item?.description);
  if (title.startsWith('loan - ')) {
    const rawLabel = title.slice('loan - '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }
  if (description.startsWith('loan deduction for ')) {
    const rawLabel = description.slice('loan deduction for '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }
  return false;
};

export const computeSavedPayrollDisplayValues = ({
  record,
  displayedSss,
  displayedTax,
  loanSummary
}) => {
  const safeRecord = record || {};
  const tilesDed = safeRecord?.tiles?.deductions || {};
  const tilesNet = safeRecord?.tiles?.net_pay || {};
  const num = (v) => (v !== undefined && v !== null && !Number.isNaN(parseFloat(v)) ? parseFloat(v) : null);
  const nv = (v) => (v !== null && v !== undefined ? Number(v) || 0 : 0);
  const tileOr = (first, second, third) => {
    const a = num(first);
    if (a !== null) return a;
    const b = num(second);
    if (b !== null) return b;
    const c = num(third);
    return c !== null ? c : 0;
  };
  const effectiveLoanSummary = normalizePayrollLoanSummary(
    buildPayrollLoanSummaryFromRecord(safeRecord) || loanSummary || {}
  );
  const shownSss = safeRecord?.deduction_settings?.sss !== false
    ? nv(tileOr(tilesDed?.sss, displayedSss, safeRecord?.sss_contribution))
    : 0;
  const shownTax = safeRecord?.deduction_settings?.tax !== false
    ? nv(tileOr(tilesDed?.withholding_tax, displayedTax, safeRecord?.withholding_tax))
    : 0;
  const rawTileTotalDed = num(tilesDed?.total_deductions);
  const rawTileNetTotalDed = num(tilesNet?.total_deductions);
  const baseTotalDeductions = nv(rawTileTotalDed !== null
    ? rawTileTotalDed
    : (rawTileNetTotalDed !== null ? rawTileNetTotalDed : safeRecord?.total_deductions));
  const rawTileNetPay = num(tilesNet?.net_pay);
  const baseNetPay = nv(rawTileNetPay !== null ? rawTileNetPay : safeRecord?.net_pay);

  return {
    displayedSss: shownSss,
    displayedTax: shownTax,
    displayTotalDeductions: Math.max(0, baseTotalDeductions),
    displayNetPay: Math.max(0, baseNetPay),
    loanSummary: effectiveLoanSummary
  };
};

const SavedPayrollPayslipView = ({
  record,
  className = 'bg-white p-8 border-2 border-gray-300 rounded-lg'
}) => {
  const safeRecord = record || {};
  const tilesIncome = safeRecord?.tiles?.income_breakdown || {};
  const tilesDed = safeRecord?.tiles?.deductions || {};
  const tilesNet = safeRecord?.tiles?.net_pay || {};
  const num = (v) => {
    if (v === undefined || v === null || Number.isNaN(v)) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const p = parseFloat(String(v).replace(/,/g, '').trim());
    return Number.isFinite(p) ? p : null;
  };
  // tiles-only + record-only fallback (no override props, no sss/tax flag zeroing)
  const fromTiles = (tile, fallback) => {
    const t = num(tile);
    if (t !== null) return t;
    const f = num(fallback);
    return f !== null ? f : 0;
  };

  const shownDaysAbsent = num(tilesIncome?.days_absent) ?? n(safeRecord?.days_absent);
  const shownDaysPresent = num(tilesIncome?.days_present) ?? n(safeRecord?.days_present);
  const shownHalfDaysPresent = num(tilesIncome?.half_days_present) ?? n(safeRecord?.half_days_present);
  const shownAbsenceDeduction = fromTiles(tilesIncome?.absence, safeRecord?.absence_deduction);
  const shownMinutesLate = num(tilesIncome?.minutes_late) ?? n(safeRecord?.minutes_late);
  const shownTardinessDeduction = fromTiles(tilesIncome?.tardiness, safeRecord?.tardiness_deduction);
  const shownMinutesUndertime = num(tilesIncome?.minutes_undertime) ?? n(safeRecord?.minutes_undertime);
  const shownUndertimeDeduction = fromTiles(tilesIncome?.undertime, safeRecord?.undertime_deduction);
  const shownHalfMonthSalary = fromTiles(tilesIncome?.half_month_salary, safeRecord?.half_month_salary);
  const shownBasicSalary = fromTiles(tilesIncome?.basic_salary ?? tilesNet?.basic_salary, safeRecord?.basic_salary);
  const shownSss = fromTiles(tilesDed?.sss, safeRecord?.sss_contribution);
  const shownHdmf = fromTiles(tilesDed?.hdmf, safeRecord?.hdmf_contribution);
  const shownPhic = fromTiles(tilesDed?.philhealth, safeRecord?.philhealth_contribution);
  const shownTax = fromTiles(tilesDed?.withholding_tax, safeRecord?.withholding_tax);
  const rawTileTotalDed = num(tilesDed?.total_deductions);
  const rawTileNetTotalDed = num(tilesNet?.total_deductions);
  const shownTotalDeductions = rawTileTotalDed !== null ? rawTileTotalDed : (rawTileNetTotalDed !== null ? rawTileNetTotalDed : n(safeRecord?.total_deductions));
  const rawTileNetPay = num(tilesNet?.net_pay);
  const shownNetPay = rawTileNetPay !== null ? rawTileNetPay : n(safeRecord?.net_pay);
  const rawTileGross = num(tilesIncome?.gross_salary) ?? num(tilesNet?.gross_salary);
  const shownGrossSalary = rawTileGross !== null ? rawTileGross : n(safeRecord?.gross_salary);
  const additionalIncome = Array.isArray(safeRecord?.additional_income) ? safeRecord.additional_income : [];
  const additionalDeductions = Array.isArray(safeRecord?.additional_deductions) ? safeRecord.additional_deductions : [];
  const holidays = Array.isArray(safeRecord?.holidays) ? safeRecord.holidays : [];
  const persistedLoanSummary = buildPayrollLoanSummaryFromRecord(safeRecord);
  const effectiveLoanSummary = normalizePayrollLoanSummary(persistedLoanSummary || { rows: [] });
  const loanRows = Array.isArray(effectiveLoanSummary?.rows) ? effectiveLoanSummary.rows : [];
  const loansToShow = loanRows.filter((r) => loanRowAmount(r) > 0);
  const hasLoanDetail = loansToShow.length > 0;
  const visibleAdditionalDeductions = hasLoanDetail
    ? additionalDeductions.filter((item) => item?.is_loan_deduction !== true)
    : additionalDeductions;
  const hideAttendanceLess = isInactivePayslipExceptionEmail(safeRecord?.employee_email);
  const showSss = shownSss > 0;
  const showHdmf = shownHdmf > 0;
  const showPhic = shownPhic > 0;
  const showTax = shownTax > 0;

  return (
    <div className={className}>
      <SavedPayslipHeader record={safeRecord} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-green-800">Income Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {holidays.length > 0 ? (
              <div className="border-b pb-2 mb-2">
                <p className="text-xs font-semibold text-gray-700 mb-1">Holidays:</p>
                <div className="space-y-1">
                  {holidays.map((holiday, idx) => (
                    <div key={`${safeRecord?.id || 'payroll'}_holiday_${idx}`} className="text-xs text-gray-700">
                      {String(holiday?.date || '')}{holiday?.name ? ` — ${String(holiday.name)}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-between">
              <span>Half Month Salary:</span>
              <span className="font-medium">{formatCurrency(shownHalfMonthSalary)}</span>
            </div>

            {!hideAttendanceLess ? (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-red-700 mb-1">Less:</p>
                <div className="flex justify-between pl-2 text-xs">
                  <span>Absence ({shownDaysAbsent || 0} days):</span>
                  <span className="text-red-600">-{formatCurrency(shownAbsenceDeduction)}</span>
                </div>
                <div className="flex justify-between pl-2 text-xs">
                  <span>Tardiness ({shownMinutesLate || 0} mins):</span>
                  <span className="text-red-600">-{formatCurrency(shownTardinessDeduction)}</span>
                </div>
                <div className="flex justify-between pl-2 text-xs">
                  <span>Undertime ({shownMinutesUndertime || 0} mins):</span>
                  <span className="text-red-600">-{formatCurrency(shownUndertimeDeduction)}</span>
                </div>
              </div>
            ) : null}

            {additionalIncome.length > 0 ? (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-green-700 mb-1">Additional Income:</p>
                {additionalIncome.map((income, idx) => (
                  <div key={`${safeRecord?.id || 'payroll'}_income_${idx}`} className="flex justify-between pl-2 text-xs">
                    <span>{itemLabel(income, 'Additional Income')}:</span>
                    <span className="text-green-600">+{formatCurrency(income?.amount)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex justify-between font-bold pt-2 border-t-2 text-base text-green-700">
              <span>Gross Salary:</span>
              <span>{formatCurrency(shownGrossSalary)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-red-800">Deductions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-xs font-semibold text-red-700 mb-2">Government Deductions:</p>
            {showSss ? (
              <div className="flex justify-between pl-2">
                <span>SSS:</span>
                <span className="text-red-600">-{formatCurrency(shownSss)}</span>
              </div>
            ) : null}
            {showHdmf ? (
              <div className="flex justify-between pl-2">
                <span>HDMF:</span>
                <span className="text-red-600">-{formatCurrency(shownHdmf)}</span>
              </div>
            ) : null}
            {showPhic ? (
              <div className="flex justify-between pl-2">
                <span>PhilHealth:</span>
                <span className="text-red-600">-{formatCurrency(shownPhic)}</span>
              </div>
            ) : null}
            {showTax ? (
              <div className="flex justify-between pl-2">
                <span>Withholding Tax:</span>
                <span className="text-red-600">-{formatCurrency(shownTax)}</span>
              </div>
            ) : null}

            {visibleAdditionalDeductions.length > 0 ? (
              <div className="border-t pt-2 mt-2">
                <p className="text-xs font-semibold text-red-700 mb-1">Additional Deductions:</p>
                {visibleAdditionalDeductions.map((deduction, idx) => (
                  <div key={`${safeRecord?.id || 'payroll'}_deduction_${idx}`} className="flex justify-between pl-2 text-xs">
                    <span>{itemLabel(deduction, 'Additional Deduction')}:</span>
                    <span className="text-red-600">-{formatCurrency(deduction?.amount)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex justify-between font-bold pt-2 border-t-2 text-red-700">
              <span>Total Deductions:</span>
              <span>{formatCurrency(shownTotalDeductions)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300 border-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-blue-800">Net Pay</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Gross Salary:</span>
                <span className="font-medium">{formatCurrency(shownGrossSalary)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Deductions:</span>
                <span className="font-medium text-red-600">{formatCurrency(shownTotalDeductions)}</span>
              </div>
              {hasLoanDetail ? (
                loansToShow.map((loan, idx) => {
                  const rowAmount = loanRowAmount(loan);
                  return (
                    <div
                      key={`${safeRecord?.id || 'payroll'}_loan_${loan?.loan_id || loan?.id || idx}`}
                      className="flex justify-between"
                    >
                      <span className="font-medium text-slate-700">{loanLabel(loan, idx)}</span>
                      <span className="font-medium text-red-600">-{formatCurrency(rowAmount)}</span>
                    </div>
                  );
                })
              ) : null}
            </div>
            <div className="border-t-2 border-blue-300 pt-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-blue-900 text-lg">NET PAY:</span>
                <span className="font-bold text-green-700 text-2xl">{formatCurrency(shownNetPay)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SavedPayrollPayslipView;
